import { useState, useEffect, useCallback, KeyboardEvent, MutableRefObject } from 'react';
import type { GrowthAgentConfig, WebSocketEvent, GrowthAgentLLMEvent, RateLimitWebSocketEvent } from '../types';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import StageModelsWidget from './common/StageModelsWidget';
import { useShowInternals } from '../contexts/InternalsContext';

export interface GrowthAgentSaveHandle {
  save(): Promise<void>;
}

interface GrowthAgentPanelProps {
  projectId: number;
  projectName?: string;
  projectDescription?: string;
  onRunCompleted?: () => void;
  /** Called on every growth-agent progress event — parent can use to refresh lists */
  onGrowthAgentEvent?: (event: GrowthAgentLLMEvent) => void;
  /** When true, only shows a small status badge (for the read-only project header) */
  statusOnly?: boolean;
  /** When true (requires statusOnly), also renders Run Now / Enable / Reset buttons */
  showActions?: boolean;
  /** Current project active state — shown as a toggle button alongside the action buttons */
  isProjectActive?: boolean;
  /** Called when the user clicks the project active toggle */
  onToggleProjectActive?: () => void;
  /** Ref for the parent to call save() from the project-level Save button */
  onSaveRef?: MutableRefObject<GrowthAgentSaveHandle | null>;
  /** Controlled niche description — when provided, the panel won't render its own field */
  nicheDescription?: string;
  onNicheDescriptionChange?: (v: string) => void;
  /** Controlled seed labels — when provided, the panel won't render its own field */
  seedLabels?: string[];
  onSeedLabelsChange?: (v: string[]) => void;
}

export default function GrowthAgentPanel({
  projectId, projectName, projectDescription, onRunCompleted, onGrowthAgentEvent, statusOnly, showActions, onSaveRef,
  nicheDescription: nicheDescriptionProp, onNicheDescriptionChange,
  seedLabels: seedLabelsProp, onSeedLabelsChange,
  isProjectActive, onToggleProjectActive,
}: GrowthAgentPanelProps) {
  const [config, setConfig] = useState<GrowthAgentConfig | null>(null);
  // Tracks whether a growth-agent session is currently active. Set by the
  // WebSocket listener when a `started` event arrives and cleared on
  // `session_complete` / `failed`. The old `runs` array (sourced from a
  // now-deleted `/discovery/runs` endpoint) is gone — the autonomous
  // growth agent doesn't persist run rows; each session is short-lived
  // and self-contained inside `run_agent_session`.
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [liveMessage, setLiveMessage] = useState('');
  const [health, setHealth] = useState<{
    total_categories: number;
    total_topics: number;
    total_keywords: number;
    total_unique_keywords: number;
    cross_topic_duplicates: number;
    pinned_keywords: number;
    settled_keywords: number;
    topics: Array<{
      topic_id: number;
      topic_name: string;
      category_name: string;
      keyword_count: number;
      pinned_count: number;
      duplicate_keywords: string[];
    }>;
  } | null>(null);

  // Controlled vs internal state for niche description & seed labels
  const controlledNiche = nicheDescriptionProp !== undefined;
  const controlledLabels = seedLabelsProp !== undefined;

  // Form state (internal — used when not controlled)
  const [nicheDescriptionInternal, setNicheDescriptionInternal] = useState('');
  const [seedLabelsInternal, setSeedLabelsInternal] = useState<string[]>([]);
  const [seedLabelInput, setSeedLabelInput] = useState('');
  const [seedUrls, setSeedUrls] = useState('');

  // Resolve to either controlled or internal
  const nicheDescription = controlledNiche ? nicheDescriptionProp : nicheDescriptionInternal;
  const setNicheDescription = controlledNiche ? (onNicheDescriptionChange ?? (() => {})) : setNicheDescriptionInternal;
  const seedLabels = controlledLabels ? seedLabelsProp : seedLabelsInternal;
  const setSeedLabels = controlledLabels ? (onSeedLabelsChange ?? (() => {})) : setSeedLabelsInternal;
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [maxCategories, setMaxCategories] = useState(3);
  const [maxTopics, setMaxTopics] = useState(5);
  const [maxKeywords, setMaxKeywords] = useState(20);
  const [seedKeywords, setSeedKeywords] = useState(5);
  const [maxBuilds, setMaxBuilds] = useState(3);
  const [maxReviews, setMaxReviews] = useState(5);
  const [allowModifyCategories, setAllowModifyCategories] = useState(false);
  const [allowModifyTopics, setAllowModifyTopics] = useState(false);
  const [allowModifyKeywords, setAllowModifyKeywords] = useState(false);
  const [allowBuildPages, setAllowBuildPages] = useState(false);
  const [allowReviewPages, setAllowReviewPages] = useState(false);
  const [labelSaving, setLabelSaving] = useState(false);
  const showInternals = useShowInternals();
  const [rateLimitWarning, setRateLimitWarning] = useState<string | null>(null);

  const { subscribe } = useWebSocket();

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configData, healthData] = await Promise.all([
        api.getGrowthAgentConfig(projectId),
        api.getProjectHealth(projectId).catch(() => null),
      ]);
      setConfig(configData);
      setHealth(healthData);
      // Populate form
      setNicheDescription(configData.niche_description || '');
      setSeedLabels(configData.seed_labels || []);
      setSeedUrls((configData.seed_urls || []).join('\n'));
      setIntervalMinutes(configData.growth_agent_interval_minutes);
      setMaxCategories(configData.max_categories_per_run);
      setMaxTopics(configData.max_topics_per_category);
      setMaxKeywords(configData.max_keywords_per_topic);
      setSeedKeywords(configData.seed_keywords_per_topic);
      setMaxBuilds(configData.max_builds_per_session);
      setMaxReviews(configData.max_reviews_per_session);
      setAllowModifyCategories(configData.allow_modify_categories);
      setAllowModifyTopics(configData.allow_modify_topics);
      setAllowModifyKeywords(configData.allow_modify_keywords);
      setAllowBuildPages(configData.allow_build_pages);
      setAllowReviewPages(configData.allow_review_pages);
    } catch (err) {
      console.error('Failed to fetch growth-agent config:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (statusOnly) {
      // In status-only mode, just fetch config to know if enabled
      api.getGrowthAgentConfig(projectId).then(setConfig).catch(() => {});
    } else {
      // Full mode always renders the body; load on mount.
      fetchConfig();
    }
  }, [fetchConfig, statusOnly, projectId]);

  // WebSocket listener
  useEffect(() => {
    const unsubscribe = subscribe((event: WebSocketEvent) => {
      // Handle rate limit events
      if (event.event_type === 'rate_limit') {
        const rl = event as RateLimitWebSocketEvent;
        setRateLimitWarning(rl.message);
        setTimeout(() => setRateLimitWarning(null), 30000);
        return;
      }

      if (event.event_type !== 'growth_agent_llm') return;
      const ge = event as GrowthAgentLLMEvent;
      if (ge.project_id !== projectId) return;

      if (ge.message) setLiveMessage(ge.message);

      // Forward every event to parent for live list updates
      if (onGrowthAgentEvent) {
        onGrowthAgentEvent(ge);
      }

      if (ge.status === 'started') {
        setIsRunning(true);
      } else if (ge.status === 'session_complete' || ge.status === 'failed') {
        setIsRunning(false);
        api.getGrowthAgentConfig(projectId).then(c => setConfig(c)).catch(() => {});
        api.getProjectHealth(projectId).then(setHealth).catch(() => {});
        if (ge.status === 'session_complete' && onRunCompleted) {
          onRunCompleted();
        }
      }
    });
    return unsubscribe;
  }, [subscribe, projectId, onRunCompleted, onGrowthAgentEvent]);

  const saveConfig = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const urlList = seedUrls.split('\n').map(u => u.trim()).filter(Boolean);
      // niche_description + seed_labels are owned by ProjectSettings General;
      // this panel only persists agent-behavior knobs.
      const updated = await api.updateGrowthAgentConfig(projectId, {
        seed_urls: urlList,
        growth_agent_interval_minutes: intervalMinutes,
        max_categories_per_run: maxCategories,
        max_topics_per_category: maxTopics,
        max_keywords_per_topic: maxKeywords,
        seed_keywords_per_topic: seedKeywords,
        max_builds_per_session: maxBuilds,
        max_reviews_per_session: maxReviews,
        allow_modify_categories: allowModifyCategories,
        allow_modify_topics: allowModifyTopics,
        allow_modify_keywords: allowModifyKeywords,
        allow_build_pages: allowBuildPages,
        allow_review_pages: allowReviewPages,
      });
      setConfig(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [projectId, seedUrls, intervalMinutes, maxCategories, maxTopics, maxKeywords, seedKeywords, maxBuilds, maxReviews, allowModifyCategories, allowModifyTopics, allowModifyKeywords, allowBuildPages, allowReviewPages]);

  // Expose save method to parent via ref
  useEffect(() => {
    if (onSaveRef) {
      onSaveRef.current = { save: saveConfig };
    }
    return () => {
      if (onSaveRef) {
        onSaveRef.current = null;
      }
    };
  }, [onSaveRef, saveConfig]);

  // Auto-save seed labels
  const saveSeedLabels = useCallback(async (labels: string[]) => {
    setLabelSaving(true);
    try {
      await api.updateGrowthAgentConfig(projectId, { seed_labels: labels });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save labels');
    } finally {
      setLabelSaving(false);
    }
  }, [projectId]);

  const handleToggle = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const updated = await api.updateGrowthAgentConfig(projectId, {
        is_enabled: !config.is_enabled,
      });
      setConfig(updated);
      if (!updated.is_enabled) {
        await api.stopGrowthAgent(projectId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    try {
      // Save current form state first
      await saveConfig();
      await api.triggerGrowthAgentRun(projectId);
      setLiveMessage('Run triggered, starting within 30 seconds...');
      // Refresh config to show enabled state
      const c = await api.getGrowthAgentConfig(projectId);
      setConfig(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger run');
    }
  };

  const handleResetRun = async () => {
    try {
      await api.resetGrowthAgentRun(projectId);
      setLiveMessage('Reset - subject will be picked up on the next cycle.');
      const c = await api.getGrowthAgentConfig(projectId);
      setConfig(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset run');
    }
  };

  const handleAddLabel = () => {
    const val = seedLabelInput.trim();
    if (val && !seedLabels.includes(val)) {
      const updated = [...seedLabels, val];
      setSeedLabels(updated);
      saveSeedLabels(updated);
    }
    setSeedLabelInput('');
  };

  const handleLabelKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const removeLabel = (idx: number) => {
    const updated = seedLabels.filter((_, i) => i !== idx);
    setSeedLabels(updated);
    saveSeedLabels(updated);
  };

  // Status-only mode
  if (statusOnly) {
    if (!showActions && !isRunning && !config?.is_enabled) return null;

    const statusBadge = isRunning ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full animate-pulse text-xs">
        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Growth agent running
      </span>
    ) : config?.is_enabled ? (
      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full text-xs">Growth agent enabled</span>
    ) : null;

    if (!showActions) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs">
          {statusBadge}
          {liveMessage && showInternals && (
            <span className="text-gray-500 dark:text-gray-400 truncate">{liveMessage}</span>
          )}
        </div>
      );
    }

    return (
      <div className="mt-4 pt-4 border-t">
        <div className="flex flex-wrap items-center gap-2">
          {statusBadge}
          {liveMessage && showInternals && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{liveMessage}</span>
          )}
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            onClick={handleRunNow}
            disabled={isRunning}
            className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            Run Now
          </button>
          <button
            onClick={handleToggle}
            className={`px-3 py-1.5 rounded-md text-sm ${config?.is_enabled ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-700 dark:text-gray-300'}`}
          >
            {config?.is_enabled ? 'Disable Agent' : 'Enable Agent'}
          </button>
          <button
            onClick={handleResetRun}
            disabled={isRunning}
            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-sm"
          >
            Reset Run
          </button>
          {onToggleProjectActive && (
            <button
              onClick={onToggleProjectActive}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${isProjectActive ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            >
              {isProjectActive ? 'Subject Active' : 'Subject Inactive'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Full panel mode (inside settings)
  return (
    <div>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        <span>Growth Agent</span>
        {config?.is_enabled && (
          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full">Enabled</span>
        )}
        {isRunning && (
          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full animate-pulse">Running</span>
        )}
        {/* The growth agent delegates: a run bills GENERATE_ARTICLE and
            EDIT_ARTICLE through its write tools, not just GROWTH_CHAT_AGENT. The
            single chip here showed only the last of those, which understated
            where a run's money actually goes. */}
        {config && <StageModelsWidget surface="growth_agent" />}
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800 space-y-4">
          {loading ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : (
            <>
              {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">{error}</div>}

              {rateLimitWarning && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-3 py-2 rounded text-sm flex items-center justify-between">
                  <span>
                    {showInternals
                      ? rateLimitWarning
                      : 'Busy - the AI service is throttling us. Retrying.'}
                  </span>
                  <button onClick={() => setRateLimitWarning(null)} className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 ml-2">&times;</button>
                </div>
              )}

              {/* Live status */}
              {liveMessage && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-3 py-2 rounded text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {liveMessage}
                </div>
              )}

              {/* Run / pause / reset + health all live on the Agents card on
                   the Project view. This panel is pure config now. */}

              {/* Config Form — niche_description + seed_labels live in
                   Project → Settings → General. This panel only owns the
                   agent-behavior knobs below. */}
              <div className="space-y-3">
                {/* Enable / disable. handleToggle saves immediately and
                     hits /stop as a side-effect when the user disables. */}
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      Agent enabled
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Allow scheduled runs at the interval below. Disabling
                      stops any in-flight session at the next checkpoint.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggle}
                    disabled={saving || !config}
                    aria-pressed={!!config?.is_enabled}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                      config?.is_enabled ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        config?.is_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Seed URLs (one per line)</label>
                  <textarea
                    value={seedUrls}
                    onChange={e => setSeedUrls(e.target.value)}
                    rows={2}
                    placeholder="https://example.com/relevant-page"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Interval (min)</label>
                    <select value={intervalMinutes} onChange={e => setIntervalMinutes(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm">
                      <option value={1}>1</option>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={60}>60</option>
                      <option value={120}>120</option>
                      <option value={360}>360</option>
                      <option value={720}>720</option>
                      <option value={1440}>1440</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Max Categories</label>
                    <input type="number" min={1} max={10} value={maxCategories} onChange={e => setMaxCategories(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Max Topics</label>
                    <input type="number" min={1} max={20} value={maxTopics} onChange={e => setMaxTopics(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Max Keywords</label>
                    <input type="number" min={1} max={50} value={maxKeywords} onChange={e => setMaxKeywords(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Seed Keywords</label>
                    <input type="number" min={1} max={maxKeywords} value={seedKeywords} onChange={e => setSeedKeywords(Math.min(Number(e.target.value), maxKeywords))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" title="AI-suggested seeds per topic (rest filled by Google Ads)" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Builds/Session</label>
                    <input type="number" min={1} max={20} value={maxBuilds} onChange={e => setMaxBuilds(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" title="Max page builds the growth agent will start per session" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Reviews/Session</label>
                    <input type="number" min={1} max={20} value={maxReviews} onChange={e => setMaxReviews(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm" title="Max draft pages the growth agent will review per session" />
                  </div>
                </div>

                {/* Per-capability permission flags. The agent only sees the
                     tools for capabilities checked here; the system prompt
                     adapts to match. All default off so projects opt in
                     explicitly — Suggest Structure owns taxonomy decisions. */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Agent Permissions</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={allowModifyCategories} onChange={e => setAllowModifyCategories(e.target.checked)} className="mt-0.5" />
                      <span>
                        <span className="font-medium">Modify categories</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">create / rename / delete categories</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={allowModifyTopics} onChange={e => setAllowModifyTopics(e.target.checked)} className="mt-0.5" />
                      <span>
                        <span className="font-medium">Modify topics</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">create / rename / delete topics</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={allowModifyKeywords} onChange={e => setAllowModifyKeywords(e.target.checked)} className="mt-0.5" />
                      <span>
                        <span className="font-medium">Modify keywords</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">accept / reject / move recommendations</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={allowBuildPages} onChange={e => setAllowBuildPages(e.target.checked)} className="mt-0.5" />
                      <span>
                        <span className="font-medium">Build pages</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">queue new page builds for top opportunities</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={allowReviewPages} onChange={e => setAllowReviewPages(e.target.checked)} className="mt-0.5" />
                      <span>
                        <span className="font-medium">Score pages</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">read drafts, request refinements, promote or dismiss</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveConfig}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>


              {/* Health + run history moved to Agents card / drawer on the
                   Project view. This panel is pure config now. */}
            </>
          )}
        </div>
    </div>
  );
}
